import { Box, Card, Typography, useTheme } from "@mui/material";
import BoatImage from "assets/images/boat.png";
import {
  CreateReferralDto,
  useCreateReferralMutation
} from "lib/features/referral/referralService";
import { selectUser } from "lib/features/user/userSlice";
import { useAppSelector } from "lib/hooks";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { availabilityCardStyles } from "styles/Home/availabilityCard.styles";
import { getFormattedFHDuration } from "util/commonHelper";
import clock from "../../../assets/icons/clock_black.svg";
import DollarSign from "../../../assets/icons/dollar.svg";
import members from "../../../assets/icons/members_black.svg";
import CreateReferralModal from "./CreateReferralModal";
import { AvailabilityCardProps } from "./types";

const FareharborAvailabilityCard = ({
  item,
  referredBy,
  user_id,
  screenName
}: AvailabilityCardProps) => {

  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const theme = useTheme();

  const [price, setPrice] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const [createReferral, { isLoading }] = useCreateReferralMutation();

  const fallbackImage = item.farehabor_item_details?.image_cdn_url || BoatImage;

  const handleCreateReferral = async () => {
    if (isLoading) return;

    const userId = referredBy && referredBy > 0 ? referredBy : user?.id ?? 0;

    const newReferral: CreateReferralDto = {
      user_id: user_id ? user_id : userId,
      business_id: item?.business?.id ?? 0,
      referral_type: 4,
      external_availability_id: item.fareharbor_availabilities?.[0].pk,
      fareharbor_customer_prototype_id:
        item.fareharbor_availabilities?.[0]?.customer_type_rates?.[0]?.pk,
      item_id: Number(item.id),
      state: 1,
    };

    try {
      setModalOpen(true);
      const response = await createReferral(newReferral).unwrap();
      setModalOpen(false);
      const checkoutUrl = user_id
        ? `/checkout/${response?.id}?showBack=${true}`
        : `/checkout/${response?.id}`;
      window.open(checkoutUrl, '_blank');
    } catch (error) {
      setModalOpen(false);
      console.log("Failed to create referral: ", error);
    }
  };

  // Get business name - handle both internal and FareHarbor items
  const getBusinessName = () => {
    if (typeof item?.business === 'string') {
      return item.business;
    }
    return item?.business?.business || "";
  };

  // Get headline - prioritize item headline, fallback to business headline
  const getHeadline = () => {
    return item?.farehabor_item_details?.headline || item?.business_headline || "";
  };

  useEffect(() => {
    let calculatedPrice = Number(
      (item?.fareharbor_availabilities?.[0]?.customer_type_rates?.[0]
        ?.customer_prototype?.total ?? 0) / 100
    );

    if (item?.fareharbor_availabilities !== undefined) {
      try {
        calculatedPrice =
          item.fareharbor_availabilities[0]?.custom_field_instances.filter(
            (f: any) =>
              f.custom_field.type === "extended-option" &&
              f.custom_field.is_required
          )[0].custom_field.extended_options[0].total / 100;
      } catch { }
    }

    setPrice(calculatedPrice);
  }, [item]);

  return (
    <>
      <Card
        sx={availabilityCardStyles.card(theme)}
        onClick={() => {
          if (!isLoading) {
            handleCreateReferral();
          }
        }}
      >
        {/* Top section with image */}
        <Box sx={availabilityCardStyles.imageBox}>
          {!referredBy && item.commission && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                display: "flex",
                alignItems: "center",
                backgroundColor: "white",
                borderRadius: "15px",
                padding: "2px 5px",
              }}
            >
              <img
                src={DollarSign}
                alt="commission icon"
                style={{ width: "16px", height: "16px" }}
              />
              <Typography
                style={{
                  fontSize: "12px",
                  marginLeft: 4,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: "600",
                }}
              >
                Pays {item.commission}%
              </Typography>
            </Box>
          )}
          <img
            src={fallbackImage}
            alt={"item-image"}
            style={availabilityCardStyles.image}
          />
        </Box>

        {/* Details Section */}
        <Box sx={availabilityCardStyles.detailsBox}>
          {/* Item Name (Title) - Bold, Large */}
          {item?.farehabor_item_details?.name && (
            <Typography 
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 700,
                fontSize: "17px",
                lineHeight: "28px",
                color: "#000000",
                marginBottom: "2px",
              }}
            >
              {item?.farehabor_item_details?.name}
            </Typography>
          )}

          {/* Business Name - Regular weight, always show with reserved space */}
          <Box sx={{ minHeight: "22px", marginBottom: "4px" }}>
            <Typography 
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "15px",
                fontWeight: 400,
                lineHeight: "22px",
                color: "#000000",
              }}
            >
              {getBusinessName() || "Business Name"}
            </Typography>
          </Box>

          <Box 
            sx={{
              minHeight: "40px", 
              marginBottom: "8px",
            }}
          >
            <Typography 
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "11px",
                fontWeight: 400,
                lineHeight: "20px",
                color: "#666666",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getHeadline()}
            </Typography>
          </Box>
          <Box sx={availabilityCardStyles.infoSection(theme)}>
            {/* Ticket Price */}
            {price > 0 && (
              <Typography 
                sx={{
                  ...availabilityCardStyles.infoTypography(theme),
                  fontWeight: 600,
                  fontSize: "15px",
                }}
              >
                ${price}/Person
              </Typography>
            )}

            {/* Capacity */}
            {item.fareharbor_availabilities !== undefined &&
              item.fareharbor_availabilities[0].capacity > 1 && (
                <Typography 
                  sx={{
                    ...availabilityCardStyles.infoTypography(theme),
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <img
                    src={members}
                    alt="members icon"
                    style={{ width: "16px", height: "16px" }}
                  />
                  {item.fareharbor_availabilities[0].capacity}max
                </Typography>
              )}

            {/* Duration */}
            <Typography 
              sx={{
                ...availabilityCardStyles.infoTypography(theme),
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <img
                src={clock}
                alt="clock icon"
                style={{ width: "16px", height: "16px" }}
              />
              {item.fareharbor_availabilities !== undefined
                ? getFormattedFHDuration(
                  item.fareharbor_availabilities[0].start_at,
                  item.fareharbor_availabilities[0].end_at
                )
                : "0 hr"}
            </Typography>
          </Box>

          {/* Time Slots Section */}
          {item.fareharbor_availabilities !== undefined &&
            item?.fareharbor_availabilities?.length > 0 && (
              <Box sx={availabilityCardStyles.timeSlotsContainer}>
                <Box sx={availabilityCardStyles.timeSlotsGrid(theme)}>
                  {item.fareharbor_availabilities
                    ?.slice(0, 3)
                    .map((availability: any, index: number) => (
                      <Box
                        key={index}
                        sx={availabilityCardStyles.timeSlotBox(theme)}
                      >
                        <Typography
                          sx={{
                            ...availabilityCardStyles.timeSlotTime(theme),
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          {moment
                            .utc(availability.start_at)
                            .tz("America/New_York")
                            .format("h:mm A")}
                        </Typography>
                        <Typography
                          sx={availabilityCardStyles.timeSlotAvailability}
                        >
                          {availability.capacity + " available"}
                        </Typography>
                      </Box>
                    ))}
                </Box>

                {item.fareharbor_availabilities !== undefined &&
                  item.fareharbor_availabilities &&
                  item.fareharbor_availabilities.length > 3 && (
                    <Typography sx={availabilityCardStyles.moreTimesText}>
                      {item.fareharbor_availabilities.length === 4
                        ? `${Math.max(0, item.fareharbor_availabilities.length - 3)} more time available`
                        : `${Math.max(0, item.fareharbor_availabilities.length - 3)} more times available`}
                    </Typography>
                  )}
              </Box>
            )}

          <Typography sx={availabilityCardStyles.bookNowText(theme)}>
            {isLoading ? "Redirecting..." : ""}
          </Typography>
        </Box>
      </Card>

      {/* Referral creation modal */}
      <CreateReferralModal open={modalOpen || isLoading} close={() => setModalOpen(false)} />
    </>
  );
};

export default FareharborAvailabilityCard;